import { Geometry, MultiPolygon } from 'ol/geom';
import { FEATURE_ACTION_TYPE } from 'config';

import { makeAPICall, makeMockAPICall } from 'models/api';
import { findConfig } from 'models/layer';
import {
    API_URL,
    DATA_PROJECTION,
    MAP_PROJECTION,
    GEOSERVER_URL,
} from 'config';
import { Feature } from 'ol';
import { GeoJSON as olGeoJson } from 'ol/format';
import {
    Feature as GeoJsonFeature,
    GeoJsonProperties,
    Geometry as GeoJsonGeometry,
} from 'geojson';

export declare namespace API {
    export interface FocusArea {
        id: number;
        name: string;
        code: string;
        status: string;
        status_comment: string;
        risk_profile: string;
        bfmc_id: number;
        plan_id: string;
        global_id: string;
        created_by: string;
        modified_by: string;
        treatment_objectives: Objective[];
        treatment_strategies: Record<string, Strategy[]>;
    }

    export interface FocusAreaAuditRecord {
        id: number;
        plan_id: string;
        feature_global_id: string;
        username: string;
        change_type: string;
        changed_on: string;
        reason_for_change: string;
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }

    export interface DataUpload {
        id: number;
        upload_id: string;
        filename: string;
        upload_type: string;
        uploaded_by: string;
        bfmc_id: number;
        status: string;
        error_msg: string;
        uploaded_by_user: string;
    }
}

export interface FocusAreaAuditRecord {
    id: number;
    planId: string;
    featureGlobalId: string;
    username: string;
    changeType: string;
    changedOn: Date;
    reasonForChange: string;
}

export interface Objective {
    id: number;
    name: string;
    description: string | null;
    code: string;
}

export interface Strategy {
    id: number;
    name: string;
    description: string | null;
    code: string;
}

export interface Treatment {
    objective: Objective;
    strategy: Strategy;
}

export interface FocusArea {
    id: number;
    name: string;
    code: string;
    status: string;
    statusComment: string;
    riskProfile: string;
    bfmcId: number;
    planId: string;
    globalId: string;
    createdBy: string;
    modifiedBy: string;
    treatments?: Treatment[];
    geometry?: MultiPolygon;
}

export interface FocusAreaFilters {
    skip?: number;
    limit?: number;
    planId?: string;
    featureID?: number;
}

export interface ObjectivesStrategiesSelection {
    objectives: Objective[];
    strategies: Record<string, Strategy[]>;
}

export interface FocusAreaAuditFields {
    planId?: string;
    globalFeatureID?: string;
}

export interface FocusAreaBatchUpdate {
    bfmcId: number;
    status: string;
}

export interface FocusAreaUpload {
    bfmcId: number;
    planId: string;
    uploadType: 'APPEND' | 'REPLACE';
    dataFile: File;
    reasonForChange: string;
}

export interface GetFocusAreaUploads {
    bfmcId: number;
    planId: string;
}

const TREATMENTS_ROUTE = `${API_URL}/treatments`;
const FOCUS_AREA_ROUTE = `${API_URL}/focusareas`;

// To be used when placing constraints on fields.
export const FocusAreaConstraints = {
    name: {
        maxLength: 50,
    },
};

export const generateTreatmentReference = (
    { strategy }: Treatment,
    focusarea: FocusArea,
) => {
    return `${focusarea.code}.${strategy.code}`;
};

export const mapAPIToFocusArea = ({
    status_comment,
    risk_profile,
    treatment_objectives,
    treatment_strategies,
    bfmc_id,
    plan_id,
    created_by,
    modified_by,
    global_id,
    ...rest
}: API.FocusArea): FocusArea => ({
    ...rest,
    statusComment: status_comment,
    riskProfile: risk_profile,
    createdBy: created_by,
    modifiedBy: modified_by,
    globalId: global_id,
    treatments: treatment_objectives.reduce<Treatment[]>((acc, obj) => {
        const strategies = treatment_strategies[`${obj.id}`];
        if (strategies)
            acc.push(
                ...strategies.map<Treatment>((strategy) => ({
                    objective: obj,
                    strategy,
                })),
            );
        return acc;
    }, []),
    bfmcId: bfmc_id,
    planId: plan_id,
});

export const mapAPIToFireTrailAudit = ({
    plan_id,
    change_type,
    changed_on,
    feature_global_id,
    reason_for_change,
    ...rest
}: API.FocusAreaAuditRecord): FocusAreaAuditRecord => ({
    ...rest,
    planId: plan_id,
    changeType: change_type,
    changedOn: new Date(changed_on),
    featureGlobalId: feature_global_id,
    reasonForChange: reason_for_change,
});

export const getObjectivesAndStrategies = makeAPICall<
    ObjectivesStrategiesSelection,
    void,
    ObjectivesStrategiesSelection
>(
    () => ({
        url: `${TREATMENTS_ROUTE}/`,
    }),
    (raw) => raw,
);

export const getFocusAreas = makeAPICall<
    FocusArea[],
    FocusAreaFilters,
    API.FocusArea[]
>(
    ({ skip, limit, planId }) => {
        const params = new URLSearchParams();
        if (skip != null) params.append('skip', `${skip}`);
        if (limit != null) params.append('limit', `${limit}`);
        if (planId != null) params.append('plan_id', `${planId}`);
        return {
            url: `${FOCUS_AREA_ROUTE}/?${params.toString()}`,
        };
    },
    (raw) => raw.map((fa) => mapAPIToFocusArea(fa)),
);

export const getFocusAreaAudit = makeAPICall<
    FocusAreaAuditRecord[],
    FocusAreaAuditFields,
    API.FocusAreaAuditRecord[]
>(
    ({ planId, globalFeatureID }) => {
        return {
            url: `${FOCUS_AREA_ROUTE}/audit/${planId}/${globalFeatureID}`,
        };
    },
    (raw) => raw.map((record) => mapAPIToFireTrailAudit(record)),
);

export const getFocusAreasFromGeoserver = makeAPICall<
    FocusArea[],
    FocusAreaFilters,
    any
>(
    ({ planId }) => {
        const serviceName = findConfig('FocusArea')?.serviceName ?? '';
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: serviceName,
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            CQL_FILTER: `${planId != null ? `PlanId='${planId}'` : ''}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },

    (raw) =>
        (raw.features as any[]).map<FocusArea>(({ id, properties }) => ({
            id: +(id as string).split('.')[1],
            code: properties.Code as string,
            // WFS feature property is "BmcId" (post BFMC→BMC rename)
            bfmcId: properties.BmcId as number,
            planId: properties.PlanId,
            name: properties.Name as string,
            status: properties.Status as string,
            statusComment: properties.StatusComm as string,
            riskProfile: properties.RiskProfil as string,
            globalId: properties.GlobalId,
            createdBy: properties.CreatedBy as string,
            modifiedBy: properties.ModifiedBy as string,
        })),
);

export const deleteFocusArea = makeAPICall<
    API.ActionResponse,
    { id: number; reason_for_change: string },
    API.ActionResponse
>(
    ({ id, reason_for_change }) => ({
        url: `${FOCUS_AREA_ROUTE}/${id}`,
        requestData: {
            method: 'DELETE',
            body: JSON.stringify({ reason_for_change }),
        },
    }),
    (x) => x,
);

export const getSelectedFeatureFromGeoserver = makeAPICall<
    Feature<Geometry>,
    FocusAreaFilters,
    any
>(
    ({ featureID }) => {
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: 'risk-planning:FocusArea',
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            featureID: `${featureID}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },
    (raw) => {
        return new olGeoJson({
            dataProjection: DATA_PROJECTION,
            featureProjection: MAP_PROJECTION,
        }).readFeatures(raw)?.[0];
    },
);

export const exportData = makeAPICall<
    ArrayBuffer,
    {
        queryParams: string;
        apiEndPoint: string;
        contentType: string;
    },
    ArrayBuffer
>(
    ({ queryParams, apiEndPoint, contentType }) => {
        return {
            url: `${FOCUS_AREA_ROUTE}/${apiEndPoint}?${queryParams}`,
            dataType: 'blob',
            contentType: contentType,
        };
    },
    (data) => {
        return data;
    },
);

export const uploadFocusArea = makeAPICall<
    API.ActionResponse,
    FocusAreaUpload,
    API.ActionResponse
>(
    ({ bfmcId, planId, uploadType, dataFile, reasonForChange }) => {
        const formData = new FormData();
        formData.append('data_file', dataFile);
        formData.append('upload_type', uploadType);
        formData.append('reason_for_change', reasonForChange);
        return {
            url: `${FOCUS_AREA_ROUTE}/data-upload/${bfmcId}/${planId}`,
            contentType: 'auto',
            requestData: {
                method: 'POST',
                body: formData,
            },
        };
    },
    (data) => {
        return data;
    },
);

export interface DataUpload {
    id: number;
    uploadId: string;
    uploadType: string;
    filename: string;
    status: string;
    errorMessage: string;
    uploadedByUser: string;
}

export const mapAPIToFocusAreaUpload = ({
    upload_id,
    upload_type,
    error_msg,
    uploaded_by_user,
    ...rest
}: API.DataUpload): DataUpload => ({
    ...rest,
    uploadId: upload_id,
    uploadType: upload_type,
    errorMessage: error_msg,
    uploadedByUser: uploaded_by_user,
});

export const getFocusAreaUploads = makeAPICall<
    DataUpload[],
    GetFocusAreaUploads,
    API.DataUpload[]
>(
    ({ bfmcId, planId }) => {
        return {
            url: `${FOCUS_AREA_ROUTE}/data-upload/${bfmcId}/${planId}?limit=3`,
        };
    },
    (raw) => raw.map(mapAPIToFocusAreaUpload),
);

export interface EditFocusArea {
    feature: Feature<Geometry> | null;
}

export const editFocusAreaDialog = makeMockAPICall<
    EditFocusArea,
    EditFocusArea,
    null
>(
    () => {},
    (_, payload) => payload,
    null,
    0,
);

export interface FocusAreaUpdate {
    id: number;
    planId: string;
    feature: GeoJsonFeature<GeoJsonGeometry, GeoJsonProperties>;
    reason_for_change: string;
    action_type: FEATURE_ACTION_TYPE;
}

export const editFocusArea = makeAPICall<
    FocusArea,
    FocusAreaUpdate,
    API.FocusArea
>(
    ({ id, planId, feature, reason_for_change, action_type }) => {
        return {
            url: `${FOCUS_AREA_ROUTE}/${id}?plan_id=${planId}`,
            contentType: 'application/json',
            requestData: {
                method: 'PUT',
                body: JSON.stringify({
                    feature,
                    reason_for_change,
                    action_type,
                }),
            },
        };
    },
    (x) => mapAPIToFocusArea(x),
);

interface FocusAreaCreate {
    feature: GeoJsonFeature<GeoJsonGeometry, GeoJsonProperties>;
}

export const addFocusArea = makeAPICall<
    FocusArea,
    FocusAreaCreate,
    API.FocusArea
>(
    ({ feature }) => {
        return {
            url: `${FOCUS_AREA_ROUTE}/?plan_id=${feature.properties?.plan_id}`,
            contentType: 'application/json',
            requestData: {
                method: 'POST',
                body: JSON.stringify({ feature }),
            },
        };
    },
    (x) => mapAPIToFocusArea(x),
);
